/**
 * Represents the structure of a configuration detail object.
 */
export interface ConfigDetailType {
  /**
   * The status of the configuration.
   */
  status: string,
  /**
   * The name of the configuration.
   */
  recordName: string,
  /**
   * The error message associated with the configuration.
   */
  errorMessage: string,
  /**
   * The source configuration version.
   */
  srcRecordVersion: number,
  /**
   * The target configuration version (optional).
   */
  targetRecordVersion?: number,
  /**
   * Additional configurations (optional).
   */
  records?: Array<unknown>
}

/**
 * Represents the structure of a configuration object.
 */
export interface ConfigType {
  /**
   * The count of configurations.
   */
  recordsCount: number,
  /**
   * The type of configuration.
   */
  recordType: string,
  /**
   * The details of each configuration.
   */
  exportImportRecordDetails: ConfigDetailType[]
}

/**
 * Represents the props for a circle badge component.
 */
export interface CircleBadgeProps {
  /**
   * The count to display in the circle badge.
   */
  count: string
}

/**
 * Represents the props for an imported journey summary component.
 */
export interface ImportedJourneySummaryProps {
  /**
   * The configurations.
   */
  records: ConfigType[],
}

/**
 * Represents the props for a grid component.
 */
export interface GridProps {
  /**
   * The data for the grid.
   */
  gridData: ConfigDetailType[]
}

/**
 * Represents a simplified version of ConfigDetailType for summary purposes.
 */
export interface SummaryConfigDetailType {
  /**
   * The status of the configuration.
   */
  status: string,
  /**
   * The name of the configuration.
   */
  recordName: string,
  /**
   * The error message associated with the configuration.
   */
  errorMessage: string,
  /**
   * The source configuration version.
   */
  srcRecordVersion: number,
  /**
   * The target configuration version (optional).
   */
  targetRecordVersion?: number,
}

/**
 * Represents the props for a summary grid component.
 */
export interface SummaryGridProps {
  /**
   * The data for the grid.
   */
  gridData: SummaryConfigDetailType[],
  /**
   * The type of configuration.
   */
  recordType: string
}

/**
 * Represents labels for different properties.
 */
export enum Labels {
  CONFIG_TYPE = 'recordType',
  CONFIG_NAME = 'recordName',
  SRC_CONFIG_VERSION = 'srcRecordVersion',
  TARGET_CONFIG_VERSION = 'targetRecordVersion',
  ERROR_MESSAGE = 'errorMessage',
  STATUS = 'status'
}
